import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Upload, FileText, Brain, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle, XCircle, TrendingUp, TrendingDown,
  Minus, Zap, MessageSquare, Send, RefreshCw, X, Info,
  BarChart2, Globe, Smartphone, DollarSign, Activity,
  ArrowRight, Lightbulb, Database, Package, Clock, Eye
} from 'lucide-react';
import { resolveFilterShape, getBodyKeys, buildFilters, supportsFilters } from '../lib/filterShape';

// ─── Constants ────────────────────────────────────────────────────────────────
const AD_SERVERS = ['Google Ad Manager (GAM)', 'Xandr / AppNexus', 'SpringServe', 'FreeWheel', 'OpenX', 'Smart AdServer / Equativ', 'Kevel', 'Other'];
const ROLES      = ['Publisher / SSP', 'Ad Network', 'DSP / Buyer', 'Agency / Trading Desk', 'AdOps Analyst', 'Yield Manager'];
const GOALS      = ['Increase Fill Rate', 'Increase eCPM', 'Increase Revenue', 'Reduce Discrepancies', 'Optimize Floor Prices', 'Analyze Deal Performance', 'General Audit'];

const SEV = {
  high:   { color: 'var(--red)',    bg: 'color-mix(in srgb, var(--red) 8%, transparent)',   border: 'color-mix(in srgb, var(--red) 30%, transparent)',   badge: 'badge-red',   icon: '🔴', label: 'High' },
  medium: { color: 'var(--amber)',  bg: 'color-mix(in srgb, var(--amber) 8%, transparent)',   border: 'color-mix(in srgb, var(--amber) 25%, transparent)',  badge: 'badge-amber', icon: '🟡', label: 'Medium' },
  low:    { color: 'var(--cyan)',   bg: 'color-mix(in srgb, var(--cyan) 6%, transparent)',   border: 'color-mix(in srgb, var(--cyan) 20%, transparent)',   badge: 'badge-cyan',  icon: '🟢', label: 'Low' },
};

const HEALTH = {
  good:     { color: 'var(--green)',  bg: 'color-mix(in srgb, var(--green) 8%, transparent)',  border: 'color-mix(in srgb, var(--green) 30%, transparent)',  label: '✓ Good',     icon: CheckCircle },
  warning:  { color: 'var(--amber)',  bg: 'color-mix(in srgb, var(--amber) 8%, transparent)',  border: 'color-mix(in srgb, var(--amber) 30%, transparent)',  label: '⚠ Warning',  icon: AlertTriangle },
  critical: { color: 'var(--red)',    bg: 'color-mix(in srgb, var(--red) 8%, transparent)',  border: 'color-mix(in srgb, var(--red) 30%, transparent)',  label: '✕ Critical', icon: XCircle },
};

const CAT_ICONS = {
  'Fill Rate': Activity, 'eCPM': TrendingUp, 'Revenue': DollarSign,
  'Floor Price': BarChart2, 'Deals': Package, 'Geo Performance': Globe,
  'Device Performance': Smartphone, 'Format/Size': Database,
  'Demand Partners': Zap, 'Viewability': CheckCircle,
  'Seasonal': Clock, 'Data Quality': Info,
};

const ANALYZING_MESSAGES = [
  'Parsing report structure…',
  'Detecting column types and metrics…',
  'Calculating fill rate trends…',
  'Identifying eCPM opportunities…',
  'Analysing geo and device performance…',
  'Checking for floor price signals…',
  'Scanning deal performance…',
  'Building priority recommendations…',
  'Finalising audit report…',
];

// ─── CSV Parser ───────────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) throw new Error('CSV needs at least a header row and one data row');

  // Detect delimiter
  const firstLine = lines[0];
  let delimiter = ',';
  let max = 0;
  [',', '\t', ';', '|'].forEach(d => {
    const c = (firstLine.match(new RegExp('\\' + d, 'g')) || []).length;
    if (c > max) { max = c; delimiter = d; }
  });

  function parseLine(line) {
    const result = []; let cur = '', inQ = false;
    for (const ch of line) {
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === delimiter && !inQ) { result.push(cur.trim()); cur = ''; continue; }
      cur += ch;
    }
    result.push(cur.trim());
    return result;
  }

  const columns = parseLine(lines[0]);
  const rows = lines.slice(1).map(l => {
    const vals = parseLine(l);
    const row = {};
    columns.forEach((h, i) => { row[h] = vals[i] ?? ''; });
    return row;
  }).filter(r => Object.values(r).some(v => v !== ''));

  return { columns, rows, rowCount: rows.length };
}

// ─── File reader helpers ──────────────────────────────────────────────────────
const readAsText   = f => new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result); r.onerror = rej; r.readAsText(f); });
const readAsBase64 = f => new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(f); });

// ─── Analysis-prep helpers (mirrored from backend/routes/reportAnalyzer.js) ──
// Smart sampling + aggregates run client-side BEFORE the /analyze POST.
// This is what cut the request body from 189MB → ~160KB. The LLM still gets
// representative rows + full-dataset totals via the aggregates block.
// Keep these in sync with the backend versions if either changes.
const MAX_ROWS_ANALYSIS_FE = 1500;

function findColByNameFE(columns, ...aliases) {
  const lower = columns.map(c => String(c).toLowerCase());
  for (const a of aliases) {
    const i = lower.indexOf(a.toLowerCase());
    if (i !== -1) return columns[i];
  }
  return null;
}
function numValFE(row, col) {
  if (!col) return 0;
  const v = parseFloat(row[col]);
  return isNaN(v) ? 0 : v;
}

function smartSampleRowsFE(rows, columns, maxRows = MAX_ROWS_ANALYSIS_FE) {
  if (!Array.isArray(rows) || !rows.length) return [];

  const revCol  = findColByNameFE(columns, 'revenue', 'rev');
  const impCol  = findColByNameFE(columns, 'impression', 'impressions', 'imps');
  const reqCol  = findColByNameFE(columns, 'request', 'requests', 'reqs');
  const fillCol = findColByNameFE(columns, 'fillrate', 'fill_rate', 'fill');

  // If no primary metric columns detected, skip zero-filter and just truncate.
  const hasMetrics = revCol || impCol || reqCol;
  if (!hasMetrics) return rows.length <= maxRows ? rows : rows.slice(0, maxRows);

  const nonZero = rows.filter(r =>
    numValFE(r, revCol) > 0 || numValFE(r, impCol) > 0 || numValFE(r, reqCol) > 0
  );
  if (nonZero.length <= maxRows) return nonZero;

  const seen = new Set();
  const out  = [];
  const take = (sorted, n) => {
    for (const r of sorted) {
      if (out.length >= maxRows || n <= 0) break;
      if (!seen.has(r)) { seen.add(r); out.push(r); n--; }
    }
  };

  if (revCol) {
    take([...nonZero].sort((a, b) => numValFE(b, revCol) - numValFE(a, revCol)),
         Math.floor(maxRows * 0.40));
  }
  if (impCol && revCol) {
    const gaps = nonZero
      .filter(r => numValFE(r, impCol) > 0 && numValFE(r, revCol) === 0)
      .sort((a, b) => numValFE(b, impCol) - numValFE(a, impCol));
    take(gaps, Math.floor(maxRows * 0.20));
  }
  if (fillCol) {
    take([...nonZero].sort((a, b) => numValFE(b, fillCol) - numValFE(a, fillCol)),
         Math.floor(maxRows * 0.13));
    take(nonZero.filter(r => numValFE(r, fillCol) > 0)
                .sort((a, b) => numValFE(a, fillCol) - numValFE(b, fillCol)),
         Math.floor(maxRows * 0.13));
  }
  if (out.length < maxRows) {
    const rest = nonZero.filter(r => !seen.has(r));
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    take(rest, maxRows - out.length);
  }
  return out;
}

function computeAggregatesFE(rows, columns) {
  if (!Array.isArray(rows) || !rows.length) return null;

  const revCol     = findColByNameFE(columns, 'revenue', 'rev');
  const impCol     = findColByNameFE(columns, 'impression', 'impressions', 'imps');
  const reqCol     = findColByNameFE(columns, 'request', 'requests', 'reqs');
  const profitCol  = findColByNameFE(columns, 'profit');
  const supplyCol  = findColByNameFE(columns, 'supply_cost');
  const ecpmCol    = findColByNameFE(columns, 'ecpm');
  const fillCol    = findColByNameFE(columns, 'fillrate', 'fill_rate');
  const dateCol    = findColByNameFE(columns, 'date');

  const totals = {};
  const sumOf = (col) => col ? rows.reduce((s, r) => s + numValFE(r, col), 0) : 0;
  if (revCol)    totals.revenue     = sumOf(revCol);
  if (impCol)    totals.impressions = sumOf(impCol);
  if (reqCol)    totals.requests    = sumOf(reqCol);
  if (profitCol) totals.profit      = sumOf(profitCol);
  if (supplyCol) totals.supply_cost = sumOf(supplyCol);
  if (totals.impressions > 0 && totals.revenue !== undefined) {
    totals.weighted_ecpm = (totals.revenue / totals.impressions) * 1000;
  }
  if (totals.requests > 0 && totals.impressions !== undefined) {
    totals.weighted_fillrate_pct = (totals.impressions / totals.requests) * 100;
  }

  const totalRows = rows.length;
  const zeroRows = rows.filter(r =>
    numValFE(r, revCol) === 0 && numValFE(r, impCol) === 0 && numValFE(r, reqCol) === 0
  ).length;

  let dateRange = null;
  if (dateCol) {
    const dates = rows.map(r => r[dateCol]).filter(Boolean).map(String).sort();
    if (dates.length) dateRange = { min: dates[0], max: dates[dates.length - 1] };
  }

  const metricColSet = new Set([revCol, impCol, reqCol, profitCol, supplyCol, ecpmCol, fillCol].filter(Boolean));
  const dimensionStats = {};
  for (const col of columns) {
    if (metricColSet.has(col)) continue;
    const distinct = new Set();
    const byVal = revCol ? new Map() : null;
    for (const r of rows) {
      const v = r[col];
      if (v === null || v === undefined || v === '') continue;
      distinct.add(v);
      if (byVal) byVal.set(v, (byVal.get(v) || 0) + numValFE(r, revCol));
    }
    dimensionStats[col] = { distinct: distinct.size };
    if (byVal) {
      dimensionStats[col].top10ByRevenue = [...byVal.entries()]
        .sort((a, b) => b[1] - a[1]).slice(0, 10)
        .map(([value, revenue]) => ({ value, revenue: Number(revenue.toFixed(2)) }));
    }
  }

  return {
    totalRows, zeroRows, nonZeroRows: totalRows - zeroRows,
    totals, dateRange, dimensionStats,
  };
}

// Single source of truth for /analyze payload prep — used by both the file-upload
// flow (runAnalysis) and the api-fetch flow (runAnalysisWithData).
function prepAnalysisPayload({ columns, rows, rowCount }) {
  const originalCount = rowCount || rows.length;
  const aggregates = computeAggregatesFE(rows, columns);
  const sampledRows = smartSampleRowsFE(rows, columns, MAX_ROWS_ANALYSIS_FE);
  return { columns, rows: sampledRows, rowCount: originalCount, aggregates };
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

function ContextPanel({ context, setContext, open, setOpen }) {
  return (
    <div className="card" style={{ marginBottom: '16px', padding: 0, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Info size={14} style={{ color: 'var(--cyan)' }} />
          <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>Optional Context</span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>— tell the AI about your setup for sharper recommendations</span>
          {(context.adServer || context.role || context.goal || context.knownIssues) && (
            <span className="badge badge-cyan" style={{ fontSize: '9px' }}>Set</span>
          )}
        </div>
        {open ? <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />}
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px' }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Ad Server Platform</label>
              <select className="select-field w-full" value={context.adServer} onChange={e => setContext(c => ({ ...c, adServer: e.target.value }))}>
                <option value="">— Select ad server —</option>
                {AD_SERVERS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Your Role</label>
              <select className="select-field w-full" value={context.role} onChange={e => setContext(c => ({ ...c, role: e.target.value }))}>
                <option value="">— Select role —</option>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Primary Goal</label>
              <select className="select-field w-full" value={context.goal} onChange={e => setContext(c => ({ ...c, goal: e.target.value }))}>
                <option value="">— Select goal —</option>
                {GOALS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Known Issues / Focus Area</label>
              <input
                className="input-field w-full"
                placeholder="e.g. mobile fill rate dropping, US eCPM low..."
                value={context.knownIssues}
                onChange={e => setContext(c => ({ ...c, knownIssues: e.target.value }))}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadZone({ onFile, loading }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  async function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }

  return (
    <div
      onClick={() => !loading && inputRef.current.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragging ? 'var(--cyan)' : 'var(--border)'}`,
        borderRadius: 'var(--radius)',
        padding: '36px 20px',
        textAlign: 'center',
        cursor: loading ? 'not-allowed' : 'pointer',
        background: dragging ? 'color-mix(in srgb, var(--cyan) 4%, transparent)' : 'var(--bg-elevated)',
        transition: 'all 0.15s',
      }}
    >
      <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
        onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
      <Upload size={28} style={{ color: 'var(--text-muted)', marginBottom: '10px' }} />
      <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>
        Drop your report here or click to browse
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
        CSV or XLSX · Up to 50,000 rows · Any ad server report format
      </div>
    </div>
  );
}

function ReportPreview({ parsedData, fileName, onReset }) {
  const { columns, rows, rowCount } = parsedData;
  const preview = rows.slice(0, 5);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileText size={14} style={{ color: 'var(--cyan)' }} />
          <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>{fileName}</span>
          <span className="badge badge-muted" style={{ fontSize: '10px' }}>{rowCount.toLocaleString()} rows · {columns.length} columns</span>
        </div>
        <button onClick={onReset} className="btn btn-secondary btn-sm"><X size={11}/> Remove</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ fontSize: '11px' }}>
          <thead>
            <tr>
              {columns.map(c => <th key={c} style={{ whiteSpace: 'nowrap', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {preview.map((row, i) => (
              <tr key={i}>
                {columns.map(c => (
                  <td key={c} style={{ whiteSpace: 'nowrap', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {String(row[c] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rowCount > 5 && (
        <div style={{ padding: '8px 16px', fontSize: '10px', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
          Showing first 5 of {rowCount.toLocaleString()} rows — all rows will be analyzed
        </div>
      )}
    </div>
  );
}

function AnalyzingState() {
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setMsgIdx(i => (i + 1) % ANALYZING_MESSAGES.length), 1800);
    return () => clearInterval(iv);
  }, []);

  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <Brain size={40} style={{ color: 'var(--purple)', marginBottom: '16px', animation: 'pulse 2s infinite' }} />
      <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', marginBottom: '8px' }}>
        AI Analysing Your Report…
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px', minHeight: '20px', transition: 'all 0.3s' }}>
        {ANALYZING_MESSAGES[msgIdx]}
      </div>
      <div style={{ width: '200px', height: '3px', background: 'var(--bg-elevated)', borderRadius: '2px', margin: '0 auto', overflow: 'hidden' }}>
        <div style={{ height: '100%', background: 'var(--purple)', borderRadius: '2px', animation: 'analyzeProgress 9s linear infinite' }} />
      </div>
      <style>{`
        @keyframes analyzeProgress { 0% { width: 0%; } 90% { width: 90%; } 100% { width: 90%; } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
      `}</style>
    </div>
  );
}

function KeyMetricStrip({ keyMetrics }) {
  const items = [
    { label: 'Impressions', value: keyMetrics.totalImpressions != null ? Number(keyMetrics.totalImpressions).toLocaleString() : null },
    { label: 'Fill Rate',   value: keyMetrics.avgFillRate },
    { label: 'eCPM',        value: keyMetrics.avgECPM },
    { label: 'Revenue',     value: keyMetrics.totalRevenue },
    { label: 'Requests',    value: keyMetrics.totalRequests != null ? Number(keyMetrics.totalRequests).toLocaleString() : null },
  ].filter(i => i.value != null && i.value !== 'null');

  if (!items.length) return null;

  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
      {items.map(({ label, value }) => (
        <div key={label} style={{ padding: '8px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', textAlign: 'center' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{label}</div>
          <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

function FindingCard({ finding, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || finding.severity === 'high');
  const s = SEV[finding.severity] || SEV.low;
  const CatIcon = CAT_ICONS[finding.category] || BarChart2;

  return (
    <div style={{ border: `1px solid ${s.border}`, borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '8px' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: open ? s.bg : 'var(--bg-elevated)', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ fontSize: '14px' }}>{s.icon}</span>
        <CatIcon size={13} style={{ color: s.color, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '1px' }}>{finding.title}</div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{finding.category}</div>
        </div>
        {finding.potentialImpact && (
          <span style={{ fontSize: '10px', color: 'var(--green)', fontWeight: 700, background: 'color-mix(in srgb, var(--green) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--green) 20%, transparent)', borderRadius: '3px', padding: '2px 7px', flexShrink: 0, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {finding.potentialImpact}
          </span>
        )}
        <span className={`badge ${s.badge}`} style={{ fontSize: '9px', flexShrink: 0 }}>{s.label}</span>
        {open ? <ChevronDown size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} /> : <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
      </button>

      {open && (
        <div style={{ padding: '14px', borderTop: `1px solid ${s.border}`, background: s.bg }}>
          {finding.dataPoint && (
            <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: '10px', fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--cyan)' }}>
              📊 {finding.dataPoint}
            </div>
          )}
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '10px' }}>
            {finding.insight}
          </div>
          <div style={{ padding: '10px 12px', background: 'color-mix(in srgb, var(--green) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--green) 20%, transparent)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: '10px', color: 'var(--green)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <ArrowRight size={10} /> Recommendation
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {finding.recommendation}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PriorityActionsTable({ actions }) {
  const impactColor = { High: 'var(--red)', Medium: 'var(--amber)', Low: 'var(--green)' };
  const effortColor = { Low: 'var(--green)', Medium: 'var(--amber)', High: 'var(--red)' };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: '36px' }}>#</th>
            <th>Action</th>
            <th style={{ width: '80px' }}>Impact</th>
            <th style={{ width: '80px' }}>Effort</th>
            <th style={{ width: '110px' }}>Time to Result</th>
          </tr>
        </thead>
        <tbody>
          {actions.map(a => (
            <tr key={a.rank}>
              <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, color: 'var(--cyan)' }}>{a.rank}</td>
              <td>
                <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-primary)', marginBottom: '2px' }}>{a.action}</div>
                {a.reasoning && <div style={{ fontSize: '10px', color: 'var(--text-muted)', lineHeight: 1.4 }}>{a.reasoning}</div>}
              </td>
              <td>
                <span style={{ fontWeight: 700, fontSize: '11px', color: impactColor[a.impact] || 'var(--text-muted)' }}>{a.impact}</span>
              </td>
              <td>
                <span style={{ fontWeight: 700, fontSize: '11px', color: effortColor[a.effort] || 'var(--text-muted)' }}>{a.effort}</span>
              </td>
              <td style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{a.timeToResult || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NeedsMoreData({ items }) {
  if (!items?.length) return null;
  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{ padding: '12px 14px', background: 'color-mix(in srgb, var(--amber) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--amber) 25%, transparent)', borderRadius: 'var(--radius-sm)', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
            <Database size={12} style={{ color: 'var(--amber)' }} />
            <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--amber)' }}>{item.dimension}</span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px', lineHeight: 1.5 }}>{item.reason}</div>
          {item.howToGet && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.5 }}>
              💡 {item.howToGet}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ChatPanel({ reportSnippet, analysisSummary }) {
  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [attachedFile, setAttachedFile] = useState(null); // { fileName, columns, rows, rowCount }
  const [attachError, setAttachError] = useState(null);
  const bottomRef  = useRef();
  const fileRef    = useRef();

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const QUICK = [
    "What's the biggest issue causing revenue loss?",
    'How do I improve my mobile fill rate?',
    'Which geo should I focus on first?',
    'What floor price changes would help most?',
  ];

  async function handleAttach(file) {
    setAttachError(null);
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    try {
      if (ext === 'xlsx' || ext === 'xls') {
        // XLSX — send to backend embedded in the chat message as base64
        const b64 = await readAsBase64(file);
        setAttachedFile({ fileName: file.name, isXlsx: true, xlsxBase64: b64, columns: [], rows: [], rowCount: 0 });
        toast.success(`Attached: ${file.name}`);
      } else {
        const text = await readAsText(file);
        const parsed = parseCSV(text);
        if (!parsed.columns.length || !parsed.rows.length) throw new Error('No data found');
        setAttachedFile({ fileName: file.name, ...parsed });
        toast.success(`Attached: ${file.name} (${parsed.rowCount.toLocaleString()} rows)`);
      }
    } catch (err) {
      setAttachError(`Could not read file: ${err.message}`);
      toast.error(`Could not read file: ${err.message}`);
    }
    // Reset input so same file can be re-attached
    if (fileRef.current) fileRef.current.value = '';
  }

  function removeAttachment() {
    setAttachedFile(null);
    setAttachError(null);
  }

  async function sendMessage(text) {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    // Build display content for user bubble
    const displayContent = attachedFile
      ? `📎 ${attachedFile.fileName}\n\n${msg}`
      : msg;

    const newMessages = [...messages, { role: 'user', content: displayContent }];
    setMessages(newMessages);

    // Capture and clear attachment before async work
    const fileToSend = attachedFile;
    setAttachedFile(null);
    setLoading(true);

    try {
      const history = newMessages.slice(0, -1);
      const { data } = await axios.post('/api/report-analyzer/chat', {
        message: msg,
        history,
        reportSnippet,
        analysisSummary,
        additionalFile: fileToSend && !fileToSend.isXlsx ? {
          fileName: fileToSend.fileName,
          columns:  fileToSend.columns,
          rows:     fileToSend.rows,
          rowCount: fileToSend.rowCount,
        } : null,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Chat failed');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px', background: 'color-mix(in srgb, var(--purple) 4%, transparent)' }}>
        <MessageSquare size={14} style={{ color: 'var(--purple)' }} />
        <span style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>Ask the AI Analyst</span>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>— dig deeper into your report</span>
      </div>

      {/* Quick prompts — only before first message */}
      {messages.length === 0 && (
        <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: '6px', borderBottom: '1px solid var(--border)' }}>
          {QUICK.map(q => (
            <button key={q} onClick={() => sendMessage(q)}
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '20px', padding: '5px 12px', fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer', transition: 'all 0.1s' }}
              onMouseEnter={e => { e.target.style.borderColor = 'var(--purple)'; e.target.style.color = 'var(--purple)'; }}
              onMouseLeave={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--text-secondary)'; }}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div style={{ maxHeight: '420px', overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              {m.role === 'assistant' && (
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'color-mix(in srgb, var(--purple) 20%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '8px', flexShrink: 0, marginTop: '2px' }}>
                  <Brain size={12} style={{ color: 'var(--purple)' }} />
                </div>
              )}
              <div style={{
                maxWidth: '75%',
                padding: m.role === 'user' ? '8px 14px' : '10px 14px',
                borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: m.role === 'user' ? 'var(--accent)' : 'var(--bg-elevated)',
                color: m.role === 'user' ? 'var(--on-accent)' : 'var(--text-primary)',
                fontSize: '12px', lineHeight: 1.7,
                border: m.role === 'user' ? 'none' : '1px solid var(--border)',
                whiteSpace: 'pre-wrap',
              }}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'color-mix(in srgb, var(--purple) 20%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Brain size={12} style={{ color: 'var(--purple)' }} />
              </div>
              <div style={{ padding: '10px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '16px 16px 16px 4px', fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> Analysing…
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Attached file chip */}
      {attachedFile && (
        <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', background: 'color-mix(in srgb, var(--purple) 4%, transparent)' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '5px 10px', background: 'color-mix(in srgb, var(--purple) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--purple) 35%, transparent)', borderRadius: '6px', fontSize: '11px', color: 'var(--purple)' }}>
            <FileText size={11} />
            <span style={{ fontWeight: 600 }}>{attachedFile.fileName}</span>
            {attachedFile.rowCount > 0 && (
              <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {attachedFile.rowCount.toLocaleString()} rows</span>
            )}
            <button onClick={removeAttachment} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0', display: 'flex', alignItems: 'center', marginLeft: '2px' }}>
              <X size={11} />
            </button>
          </div>
        </div>
      )}

      {/* Attach error */}
      {attachError && (
        <div style={{ padding: '6px 16px', fontSize: '11px', color: 'var(--red)' }}>
          {attachError}
        </div>
      )}

      {/* Input row */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', alignItems: 'center' }}>
        {/* Hidden file input */}
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={e => handleAttach(e.target.files[0])}
        />
        {/* Upload button */}
        <button
          onClick={() => fileRef.current?.click()}
          disabled={loading}
          title="Attach a CSV or XLSX file"
          style={{
            background: attachedFile ? 'color-mix(in srgb, var(--purple) 15%, transparent)' : 'var(--bg-elevated)',
            border: `1px solid ${attachedFile ? 'color-mix(in srgb, var(--purple) 40%, transparent)' : 'var(--border)'}`,
            borderRadius: 'var(--radius-sm)',
            padding: '7px 9px',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            color: attachedFile ? 'var(--purple)' : 'var(--text-muted)',
          }}
        >
          <Upload size={14} />
        </button>

        <input
          className="input-field"
          style={{ flex: 1 }}
          placeholder={attachedFile ? 'Ask about the attached file…' : 'Ask a follow-up question about your report…'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          disabled={loading}
        />
        <button className="btn btn-primary" onClick={() => sendMessage()} disabled={loading || !input.trim()}>
          <Send size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── Analysis Results ─────────────────────────────────────────────────────────
function AnalysisResults({ analysis, reportSnippet, analysisSummary, onReanalyze }) {
  const { reportSummary, overallHealth, executiveSummary, findings = [], priorityActions = [], needsMoreData = [] } = analysis;
  const health = HEALTH[overallHealth] || HEALTH.warning;
  const HealthIcon = health.icon;

  const highFindings   = findings.filter(f => f.severity === 'high');
  const medFindings    = findings.filter(f => f.severity === 'medium');
  const lowFindings    = findings.filter(f => f.severity === 'low');
  const sortedFindings = [...highFindings, ...medFindings, ...lowFindings];

  const [activeTab, setActiveTab] = useState('findings');

  return (
    <div>
      {/* Executive Summary Card */}
      <div className="card" style={{ marginBottom: '16px', background: health.bg, border: `1px solid ${health.border}`, padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '10px', color: health.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              {reportSummary?.detectedType || 'Programmatic Report'}
              {reportSummary?.dateRange && ` · ${reportSummary.dateRange}`}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', marginBottom: '8px' }}>
              AI Audit Complete
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: health.bg, border: `1px solid ${health.border}`, borderRadius: 'var(--radius-sm)', flexShrink: 0 }}>
            <HealthIcon size={13} style={{ color: health.color }} />
            <span style={{ fontWeight: 800, fontSize: '12px', color: health.color }}>{health.label}</span>
          </div>
        </div>

        {/* Key metrics strip */}
        {reportSummary?.keyMetrics && <KeyMetricStrip keyMetrics={reportSummary.keyMetrics} />}

        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '12px' }}>
          {executiveSummary}
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '11px' }}>
          {highFindings.length > 0 && (
            <span style={{ color: 'var(--red)', fontWeight: 700 }}>
              🔴 {highFindings.length} High-priority finding{highFindings.length > 1 ? 's' : ''}
            </span>
          )}
          {medFindings.length > 0 && (
            <span style={{ color: 'var(--amber)', fontWeight: 700 }}>
              🟡 {medFindings.length} Medium
            </span>
          )}
          {lowFindings.length > 0 && (
            <span style={{ color: 'var(--cyan)', fontWeight: 700 }}>
              🟢 {lowFindings.length} Low
            </span>
          )}
          {priorityActions.length > 0 && (
            <span style={{ color: 'var(--green)', fontWeight: 700, marginLeft: '4px' }}>
              ✓ {priorityActions.length} priority actions
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: '16px' }}>
        <button className={`tab ${activeTab === 'findings' ? 'active' : ''}`} onClick={() => setActiveTab('findings')}>
          <Lightbulb size={12} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
          Findings
          <span className="badge badge-muted" style={{ marginLeft: '6px', fontSize: '10px' }}>{sortedFindings.length}</span>
        </button>
        <button className={`tab ${activeTab === 'actions' ? 'active' : ''}`} onClick={() => setActiveTab('actions')}>
          <Zap size={12} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
          Priority Actions
          <span className="badge badge-muted" style={{ marginLeft: '6px', fontSize: '10px' }}>{priorityActions.length}</span>
        </button>
        {needsMoreData.length > 0 && (
          <button className={`tab ${activeTab === 'moredata' ? 'active' : ''}`} onClick={() => setActiveTab('moredata')}>
            <Database size={12} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
            Needs More Data
            <span className="badge badge-amber" style={{ marginLeft: '6px', fontSize: '10px' }}>{needsMoreData.length}</span>
          </button>
        )}
      </div>

      {/* Findings tab */}
      {activeTab === 'findings' && (
        <div>
          {analysis._truncated && (
            <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
              <AlertTriangle size={12} />
              <div style={{ fontSize: '12px' }}>
                <strong>Response was truncated</strong> — the AI hit the output limit before writing all findings.
                The report summary and key metrics above were captured successfully.
                <button className="btn btn-secondary btn-sm" style={{ marginLeft: '10px' }}
                  onClick={onReanalyze}>
                  <RefreshCw size={11} /> Re-analyze
                </button>
              </div>
            </div>
          )}
          {analysis._parseError && analysis._rawAnalysis && (
            <div className="card" style={{ marginBottom: '16px', border: '1px solid color-mix(in srgb, var(--amber) 30%, transparent)', background: 'color-mix(in srgb, var(--amber) 4%, transparent)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={12} /> Raw AI Analysis — structured parsing failed
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: '400px', overflowY: 'auto' }}>
                {analysis._rawAnalysis}
              </div>
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Tip: Try re-analyzing — occasional JSON formatting issues resolve on retry.</span>
                <button className="btn btn-secondary btn-sm" onClick={onReanalyze}>
                  <RefreshCw size={11} /> Re-analyze
                </button>
              </div>
            </div>
          )}
          {sortedFindings.length === 0 && !analysis._parseError && !analysis._truncated ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              <CheckCircle size={28} style={{ color: 'var(--green)', marginBottom: '10px' }} />
              <div>No significant issues found in this report.</div>
            </div>
          ) : (
            sortedFindings.map((f, i) => <FindingCard key={f.id || i} finding={f} defaultOpen={i < 2} />)
          )}
        </div>
      )}

      {/* Actions tab */}
      {activeTab === 'actions' && (
        <div>
          {priorityActions.length > 0
            ? <PriorityActionsTable actions={priorityActions} />
            : <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No priority actions generated.</div>
          }
        </div>
      )}

      {/* More data tab */}
      {activeTab === 'moredata' && (
        <div>
          <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
            <AlertTriangle size={12} />
            <div style={{ fontSize: '11px' }}>The AI identified these additional dimensions/metrics that would enable deeper analysis. Pull these reports and re-analyze for better recommendations.</div>
          </div>
          <NeedsMoreData items={needsMoreData} />
        </div>
      )}

      {/* Chat panel */}
      <div style={{ marginTop: '24px' }}>
        <div style={{ fontSize: '10px', color: 'var(--purple)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <MessageSquare size={11} /> Follow-up Chat
        </div>
        <ChatPanel reportSnippet={reportSnippet} analysisSummary={analysisSummary} />
      </div>
    </div>
  );
}

// Sprint 2: builds a hints-shaped adapter from cfg columns (Sprint 1B backfill).
// Returning the same shape as the old PARTNER_HINTS entries means downstream
// consumers (dimension/metric pickers, filter UI) don't need to change.
// PARTNER_HINTS registry deleted; filterability now declared inline on dimensions.
function detectPartnerHints(cfg) {
  const shape = resolveFilterShape(cfg);
  const bodyKeys = getBodyKeys(shape);
  const dims = cfg?.dimensions || [];
  const metrics = cfg?.metrics || [];
  return {
    shape,
    bodyKeys,
    dimensions: dims,
    metrics,
    filters: dims.filter(d => d.filterable !== false),
    filterOperators: (cfg?.operator_vocabulary || []).map(o => o.wire),
  };
}

// ─── FetchFromAPIPanel ────────────────────────────────────────────────────────
function FetchFromAPIPanel({ onDataReady, onCancel }) {
  const [configs, setConfigs]           = useState([]);
  const [configId, setConfigId]         = useState('');
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [dateFrom, setDateFrom]         = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo]             = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0];
  });
  const [bodyRaw, setBodyRaw]           = useState('{}');
  const [bodyErr, setBodyErr]           = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [step, setStep]                 = useState('config');
  const [previewing, setPreviewing]     = useState(false);
  const [previewData, setPreviewData]   = useState(null);
  const [selectedCols, setSelectedCols] = useState([]);
  const [fetchingFull, setFetchingFull] = useState(false);

  const selectedConfig = configs.find(c => String(c.id) === String(configId));
  const hints = detectPartnerHints(selectedConfig);

  useEffect(() => {
    axios.get('/api/partner-configs')
      .then(r => {
        // Report Analyzer is built for Ad Server reports only — filter out
        // demand/supply configs so they never appear in the dropdown.
        // Disclaimer in the parent component explains why.
        const cfgs = (r.data || []).filter(c => c.role === 'adserver');
        setConfigs(cfgs);
        if (cfgs.length) {
          setConfigId(String(cfgs[0].id));
          const rb = cfgs[0].request_body;
          setBodyRaw(rb && Object.keys(rb).length ? JSON.stringify(rb, null, 2) : '{}');
        }
      })
      .catch(() => {})
      .finally(() => setLoadingConfigs(false));
  }, []);

  function switchConfig(id) {
    setConfigId(id);
    setStep('config'); setPreviewData(null); setActiveFilters([]);
    const cfg = configs.find(c => String(c.id) === id);
    const rb  = cfg?.request_body;
    setBodyRaw(rb && Object.keys(rb).length ? JSON.stringify(rb, null, 2) : '{}');
  }

  function parseBody() {
    try { const p = JSON.parse(bodyRaw); setBodyErr(null); return p; }
    catch (e) { setBodyErr('Invalid JSON: ' + e.message); return null; }
  }

  // ── Dimension pill click ────────────────────────────────────────────────────
  function insertDimension(key) {
    const parsed = parseBody(); if (!parsed) return;
    const dimKey = parsed.hasOwnProperty('dimensions') ? 'dimensions'
                 : parsed.hasOwnProperty('split_by')   ? 'split_by'
                 : parsed.hasOwnProperty('groupBy')    ? 'groupBy'
                 : hints.bodyKeys?.dimensions || 'dimensions';
    if (!Array.isArray(parsed[dimKey])) parsed[dimKey] = [];
    if (!parsed[dimKey].includes(key)) { parsed[dimKey].push(key); setBodyRaw(JSON.stringify(parsed, null, 2)); toast.success(`Added "${key}" to ${dimKey}`); }
    else toast(`"${key}" already in ${dimKey}`, { icon: 'ℹ️' });
  }
  function removeDimension(key) {
    const parsed = parseBody(); if (!parsed) return;
    ['dimensions','split_by','groupBy'].forEach(k => { if (Array.isArray(parsed[k])) parsed[k] = parsed[k].filter(v => v !== key); });
    setBodyRaw(JSON.stringify(parsed, null, 2));
  }

  // ── Metric pill click ───────────────────────────────────────────────────────
  function insertMetric(key) {
    const parsed = parseBody(); if (!parsed) return;
    const metKey = parsed.hasOwnProperty('metrics')     ? 'metrics'
                 : parsed.hasOwnProperty('data_fields') ? 'data_fields'
                 : parsed.hasOwnProperty('fields')      ? 'fields'
                 : hints.bodyKeys?.metrics || 'metrics';
    if (!Array.isArray(parsed[metKey])) parsed[metKey] = [];
    if (!parsed[metKey].includes(key)) { parsed[metKey].push(key); setBodyRaw(JSON.stringify(parsed, null, 2)); toast.success(`Added "${key}" to ${metKey}`); }
    else toast(`"${key}" already in ${metKey}`, { icon: 'ℹ️' });
  }
  function removeMetric(key) {
    const parsed = parseBody(); if (!parsed) return;
    ['metrics','data_fields','fields'].forEach(k => { if (Array.isArray(parsed[k])) parsed[k] = parsed[k].filter(v => v !== key); });
    setBodyRaw(JSON.stringify(parsed, null, 2));
  }

  // ── Filter helpers ──────────────────────────────────────────────────────────
  function applyFiltersToBody(filters) {
    const parsed = parseBody(); if (!parsed) return;
    delete parsed.filters; delete parsed.filterBy;

    const withValues = filters.filter(f => f.value?.trim());
    if (!withValues.length) { setBodyRaw(JSON.stringify(parsed, null, 2)); return; }

    // Build filters in the partner's wire shape (PAS uses values:[...], Criteo uses {name,value,match},
    // Sharethrough uses {field,value,operator}). Dispatcher handles the IN-conversion for multi-value PAS.
    const shape = hints.shape;
    if (supportsFilters(shape)) {
      const entries = buildFilters(shape, withValues);
      if (shape === 'json-field-value-operator') {
        parsed.filterBy = entries;
      } else {
        parsed.filters = entries;
      }
    }

    // PAS-specific pagination defaults — distinct from filter shape, kept as shape-conditional.
    if (shape === 'json-values-array') {
      if (!parsed.limit) parsed.limit = '10000';
      if (!parsed.skip)  parsed.skip  = '0';
    }

    setBodyRaw(JSON.stringify(parsed, null, 2));
  }
  function addFilter(key) {
    if (activeFilters.find(f => f.key === key)) return;
    const updated = [...activeFilters, { key, value: '', operator: '=' }];
    setActiveFilters(updated); applyFiltersToBody(updated);
  }
  function removeFilter(key) {
    const updated = activeFilters.filter(f => f.key !== key);
    setActiveFilters(updated); applyFiltersToBody(updated);
  }
  function updateFilter(key, field, val) {
    const updated = activeFilters.map(f => f.key === key ? { ...f, [field]: val } : f);
    setActiveFilters(updated); applyFiltersToBody(updated);
  }

  // ── Compute which keys are already active in the body ─────────────────────
  function getActiveDims() {
    try {
      const p = JSON.parse(bodyRaw);
      return [...(p.dimensions||[]), ...(p.split_by||[]), ...(p.groupBy||[])];
    } catch { return []; }
  }
  function getActiveMetrics() {
    try {
      const p = JSON.parse(bodyRaw);
      return [...(p.metrics||[]), ...(p.data_fields||[]), ...(p.fields||[])];
    } catch { return []; }
  }

  // ── Preview / fetch ─────────────────────────────────────────────────────────
  async function handlePreview() {
    const extra = parseBody(); if (!extra) return;
    setPreviewing(true); setPreviewData(null);
    try {
      const { data } = await axios.post('/api/report-analyzer/api-fetch',
        { configId, dateFrom, dateTo, extraParams: extra, previewOnly: true },
        { timeout: 35000 });
      setPreviewData(data);
      setSelectedCols(data.columns);
      setStep('preview');
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.hint || 'Preview failed — check token and date range');
    } finally { setPreviewing(false); }
  }

  async function handleFetchAndAnalyze() {
    const extra = parseBody(); if (!extra) return;
    setFetchingFull(true);
    try {
      const { data } = await axios.post('/api/report-analyzer/api-fetch',
        { configId, dateFrom, dateTo, extraParams: extra, previewOnly: false },
        { timeout: 60000 });

      // If user came straight here without previewing, use all columns
      const cols = selectedCols.length ? selectedCols : data.columns;

      const filteredRows = data.rows.map(row => {
        const out = {}; cols.forEach(c => { out[c] = row[c]; }); return out;
      });
      toast.success(`Fetched ${data.totalRows.toLocaleString()} rows`);
      onDataReady({ columns: cols, rows: filteredRows, rowCount: data.totalRows,
        fileName: `${selectedConfig?.name || 'api'} (${dateFrom} → ${dateTo})`, sourceType: 'api' });
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.hint || 'Fetch failed');
    } finally { setFetchingFull(false); }
  }

  if (loadingConfigs) return (
    <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
      <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading configs…
    </div>
  );
  if (!configs.length) return (
    <div>
      <div className="alert alert-warning"><AlertTriangle size={12} />
        <div style={{ fontSize: '12px' }}>No Ad Server configs found. Add one in <strong>Partner Configs</strong> (with role set to <em>Ad Server</em>) to use API fetch.</div>
      </div>
      <button className="btn btn-secondary btn-sm" onClick={onCancel} style={{ marginTop: '8px' }}>Cancel</button>
    </div>
  );

  const activeDims    = getActiveDims();
  const activeMetrics = getActiveMetrics();

  return (
    <div>
      {/* Config + date row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px auto auto', gap: '8px', alignItems: 'end', marginBottom: '14px' }}>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', fontWeight: 700 }}>API Config</div>
          <select className="select-field w-full" value={configId} onChange={e => switchConfig(e.target.value)}>
            {configs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', fontWeight: 700 }}>From</div>
          <input type="date" className="input-field" value={dateFrom} max={dateTo}
            onChange={e => { setDateFrom(e.target.value); setStep('config'); setPreviewData(null); }}
            style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }} />
        </div>
        <div>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', fontWeight: 700 }}>To</div>
          <input type="date" className="input-field" value={dateTo} min={dateFrom}
            max={new Date(new Date().setDate(new Date().getDate()-1)).toISOString().split('T')[0]}
            onChange={e => { setDateTo(e.target.value); setStep('config'); setPreviewData(null); }}
            style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }} />
        </div>
        <button className="btn btn-secondary" onClick={handlePreview} disabled={previewing || fetchingFull || !configId}
          title="Fetch 5 rows to verify data before full analysis" style={{ whiteSpace: 'nowrap' }}>
          {previewing ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Previewing…</> : <><Eye size={12} /> Preview</>}
        </button>
        <button className="btn btn-primary" onClick={handleFetchAndAnalyze} disabled={previewing || fetchingFull || !configId}
          title="Fetch all data and run AI analysis immediately" style={{ whiteSpace: 'nowrap' }}>
          {fetchingFull ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Fetching…</> : <><Brain size={12} /> Fetch &amp; Analyze</>}
        </button>
      </div>

      {/* Dimensions */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--cyan)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Dimensions</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {hints.dimensions.map(d => {
            const active = activeDims.includes(d.key);
            return (
              <button key={d.key} onClick={() => active ? removeDimension(d.key) : insertDimension(d.key)}
                style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', cursor: 'pointer',
                  border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
                  background: active ? 'color-mix(in srgb, var(--cyan) 12%, transparent)' : 'var(--bg-base)',
                  color: active ? 'var(--cyan)' : 'var(--text-muted)', fontWeight: active ? 700 : 400 }}>
                {active ? `✓ ${d.label}` : `+ ${d.label}`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Metrics */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Metrics</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {hints.metrics.map(m => {
            const active = activeMetrics.includes(m.key);
            return (
              <button key={m.key} onClick={() => active ? removeMetric(m.key) : insertMetric(m.key)}
                style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', cursor: 'pointer',
                  border: `1px solid ${active ? 'var(--green)' : 'var(--border)'}`,
                  background: active ? 'color-mix(in srgb, var(--green) 10%, transparent)' : 'var(--bg-base)',
                  color: active ? 'var(--green)' : 'var(--text-muted)', fontWeight: active ? 700 : 400 }}>
                {active ? `✓ ${m.label}` : `+ ${m.label}`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Filters</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: activeFilters.length ? '8px' : 0 }}>
          {hints.filters.map(f => {
            const active = activeFilters.find(af => af.key === f.key);
            return (
              <button key={f.key} onClick={() => active ? removeFilter(f.key) : addFilter(f.key)}
                title={f.hint}
                style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', cursor: 'pointer',
                  border: `1px solid ${active ? 'var(--amber)' : 'var(--border)'}`,
                  background: active ? 'color-mix(in srgb, var(--amber) 10%, transparent)' : 'var(--bg-base)',
                  color: active ? 'var(--amber)' : 'var(--text-muted)', fontWeight: active ? 700 : 400 }}>
                {active ? `✓ ${f.label}` : `+ ${f.label}`}
              </button>
            );
          })}
        </div>
        {activeFilters.map(f => {
          const hint = hints.filters.find(h => h.key === f.key);
          const operators = hints.filterOperators || ['=', '!=', 'LIKE', 'IN'];
          return (
            <div key={f.key} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '5px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', color: 'var(--amber)', minWidth: '100px', fontWeight: 600 }}>{hint?.label || f.key}</span>
              <select className="select-field" style={{ fontSize: '11px', padding: '3px 6px', height: '28px', width: '80px' }}
                value={f.operator || '='} onChange={e => updateFilter(f.key, 'operator', e.target.value)}>
                {operators.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
              <input className="input-field" style={{ flex: 1, minWidth: '140px', fontSize: '11px', padding: '4px 8px' }}
                placeholder={hint?.hint || f.key}
                value={f.value}
                onChange={e => updateFilter(f.key, 'value', e.target.value)} />
              <button onClick={() => removeFilter(f.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: '2px 4px' }}>
                <X size={12} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Raw JSON editor — collapsible */}
      <details style={{ marginBottom: '12px' }}>
        <summary style={{ fontSize: '11px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
          Raw request body JSON (auto-updated by pills above)
        </summary>
        <textarea
          className="input-field w-full"
          style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', minHeight: '100px', marginTop: '6px' }}
          value={bodyRaw}
          onChange={e => { setBodyRaw(e.target.value); setBodyErr(null); }} />
        {bodyErr && <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '3px' }}>{bodyErr}</div>}
      </details>

      {/* Preview results */}
      {step === 'preview' && previewData && (
        <div style={{ marginBottom: '12px', padding: '12px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--green)' }}>✓ Connected</strong> — {previewData.totalRows.toLocaleString()} rows · {previewData.columns.length} columns
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{selectedCols.length} of {previewData.columns.length} columns selected for analysis</div>
          </div>
          {/* Column selector */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px', display: 'flex', gap: '10px' }}>
              <span>Columns to include in analysis</span>
              <button onClick={() => setSelectedCols(previewData.columns)} style={{ fontSize: '10px', color: 'var(--cyan)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>All</button>
              <button onClick={() => setSelectedCols([])} style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>None</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {previewData.columns.map(col => {
                const sel = selectedCols.includes(col);
                return (
                  <button key={col} onClick={() => setSelectedCols(prev => sel ? prev.filter(c => c !== col) : [...prev, col])}
                    style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', cursor: 'pointer',
                      border: `1px solid ${sel ? 'var(--cyan)' : 'var(--border)'}`,
                      background: sel ? 'color-mix(in srgb, var(--cyan) 10%, transparent)' : 'var(--bg-base)',
                      color: sel ? 'var(--cyan)' : 'var(--text-muted)', fontWeight: sel ? 700 : 400 }}>
                    {col}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Preview table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ fontSize: '11px' }}>
              <thead><tr>{selectedCols.slice(0, 8).map(c => <th key={c}>{c}</th>)}{selectedCols.length > 8 && <th>+{selectedCols.length - 8}</th>}</tr></thead>
              <tbody>{previewData.previewRows.map((row, i) => (
                <tr key={i}>{selectedCols.slice(0, 8).map(c => <td key={c} className="mono" style={{ color: 'var(--text-secondary)' }}>{String(row[c] ?? '—').slice(0, 40)}</td>)}{selectedCols.length > 8 && <td>…</td>}</tr>
              ))}</tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setStep('config'); setPreviewData(null); }}>← Back</button>
            <button className="btn btn-primary" onClick={handleFetchAndAnalyze} disabled={fetchingFull || !selectedCols.length}>
              {fetchingFull ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Fetching…</> : <><Brain size={12} /> Fetch &amp; Analyze</>}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
        <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}


export default function ReportAnalyzer() {
  const [context, setContext]       = useState({ adServer: '', role: '', goal: '', knownIssues: '' });
  const [contextOpen, setContextOpen] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [fileName, setFileName]     = useState('');
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'api'
  const [xlsxBase64, setXlsxBase64] = useState(null);
  const [analyzing, setAnalyzing]   = useState(false);
  const [analysis, setAnalysis]     = useState(null);
  const [reportSnippet, setReportSnippet]     = useState('');
  const [analysisSummary, setAnalysisSummary] = useState('');
  const [error, setError]           = useState(null);

  // Called by FetchFromAPIPanel when data is ready
  function handleApiData({ columns, rows, rowCount, fileName: fn }) {
    setError(null); setAnalysis(null); setXlsxBase64(null);
    setFileName(fn); setUploadMode('file'); // switch back so analyze flow works
    setParsedData({ columns, rows, rowCount });
    // Auto-trigger analysis immediately
    setTimeout(() => runAnalysisWithData({ columns, rows, rowCount, fn }), 100);
  }

  async function runAnalysisWithData({ columns, rows, rowCount, fn }) {
    setAnalyzing(true); setError(null); setAnalysis(null);
    try {
      // Smart-sample + zero-filter + compute aggregates client-side BEFORE upload.
      // Backend uses MAX_ROWS_ANALYSIS=1500; we ship at most that many rows + aggregates.
      const prepped = prepAnalysisPayload({ columns, rows, rowCount });
      const { data } = await axios.post('/api/report-analyzer/analyze', {
        context, fileName: fn,
        inputType: 'csv',
        columns:    prepped.columns,
        rows:       prepped.rows,
        rowCount:   prepped.rowCount,
        aggregates: prepped.aggregates,
      }, { timeout: 0 });
      setAnalysis(data.analysis);
      setReportSnippet(data.reportSnippet || '');
      setAnalysisSummary(data.analysisSummary || '');
      toast.success('Analysis complete');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Analysis failed';
      setError(msg); toast.error(msg);
    } finally { setAnalyzing(false); }
  }

  async function handleFile(file) {
    setError(null);
    setAnalysis(null);
    setParsedData(null);
    setFileName(file.name);

    const ext = file.name.split('.').pop().toLowerCase();
    try {
      if (ext === 'xlsx' || ext === 'xls') {
        // XLSX: send to backend for parsing during analysis
        const b64 = await readAsBase64(file);
        setXlsxBase64(b64);
        // Set a placeholder parsed data for preview
        setParsedData({ columns: ['Loading…'], rows: [], rowCount: 0, isXlsx: true });
        toast.success('XLSX file loaded — click Analyze to continue');
      } else {
        // CSV: parse on frontend
        const text = await readAsText(file);
        const parsed = parseCSV(text);
        if (parsed.columns.length === 0) throw new Error('No columns detected');
        if (parsed.rows.length === 0)    throw new Error('No data rows found');
        setXlsxBase64(null);
        setParsedData(parsed);
        toast.success(`Loaded ${parsed.rowCount.toLocaleString()} rows · ${parsed.columns.length} columns`);
      }
    } catch (err) {
      toast.error(`Failed to read file: ${err.message}`);
      setError(err.message);
    }
  }

  async function runAnalysis() {
    if (!parsedData && !xlsxBase64) return;
    setAnalyzing(true);
    setError(null);
    setAnalysis(null);

    try {
      const payload = {
        context,
        fileName,
      };

      if (xlsxBase64) {
        // XLSX flow: backend parses + computes aggregates + smart-samples
        // server-side. Frontend can't pre-process because rows live there.
        payload.inputType = 'xlsx';
        payload.xlsxBase64 = xlsxBase64;
      } else {
        // CSV flow: smart-sample + aggregates client-side BEFORE POST.
        const prepped = prepAnalysisPayload(parsedData);
        payload.inputType  = 'csv';
        payload.columns    = prepped.columns;
        payload.rows       = prepped.rows;
        payload.rowCount   = prepped.rowCount;
        payload.aggregates = prepped.aggregates;
      }

      const { data } = await axios.post('/api/report-analyzer/analyze', payload, { timeout: 130000 });
      setAnalysis(data.analysis);
      setReportSnippet(data.reportSnippet || '');
      setAnalysisSummary(data.analysisSummary || '');
      toast.success('Analysis complete');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Analysis failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setAnalyzing(false);
    }
  }

  function reset() {
    setParsedData(null);
    setXlsxBase64(null);
    setFileName('');
    setAnalysis(null);
    setError(null);
    setReportSnippet('');
    setAnalysisSummary('');
  }

  const canAnalyze = (parsedData && !parsedData.isXlsx && parsedData.rows.length > 0) || xlsxBase64;

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Report Analyzer</h1>
          <span style={{ fontSize: '10px', fontWeight: 800, background: 'color-mix(in srgb, var(--purple) 20%, transparent)', color: 'var(--purple)', border: '1px solid color-mix(in srgb, var(--purple) 40%, transparent)', borderRadius: '4px', padding: '2px 8px' }}>BETA</span>
          <span style={{ fontSize: '10px', fontWeight: 800, background: 'color-mix(in srgb, var(--green) 10%, transparent)', color: 'var(--green)', border: '1px solid color-mix(in srgb, var(--green) 30%, transparent)', borderRadius: '4px', padding: '2px 8px' }}>NEW</span>
        </div>
        <p className="page-subtitle" style={{ margin: 0 }}>
          Upload any ad server or SSP report — AI analyzes fill rate, eCPM, revenue opportunities, and gives you a ranked action plan
        </p>
      </div>

      {/* Not yet analyzing / no result yet */}
      {!analyzing && !analysis && (
        <>
          <ContextPanel context={context} setContext={setContext} open={contextOpen} setOpen={setContextOpen} />

          {!parsedData && !xlsxBase64 ? (
            <div>
              {/* Upload method toggle */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                {[['file', '📁 Upload File', 'CSV or XLSX from your ad server'], ['api', '⚡ Fetch from API', 'Use a saved Fetch Engine config — best for large datasets']].map(([m, label, tip]) => (
                  <button key={m} title={tip}
                    onClick={() => setUploadMode(m)}
                    style={{ fontSize: '12px', fontWeight: uploadMode === m ? 700 : 400, color: uploadMode === m ? 'var(--bg-base)' : 'var(--text-secondary)', background: uploadMode === m ? 'var(--cyan)' : 'var(--bg-elevated)', border: `1px solid ${uploadMode === m ? 'var(--cyan)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {label}
                  </button>
                ))}
              </div>
              {uploadMode === 'api' && (
                <div className="card" style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <Zap size={13} style={{ color: 'var(--cyan)' }} />
                    <div style={{ fontWeight: 700, fontSize: '13px' }}>Fetch Report via API</div>
                    <div className="badge badge-muted" style={{ fontSize: '10px', marginLeft: '4px' }}>Recommended for large datasets</div>
                  </div>
                  {/* Ad-Server-only disclaimer for API mode */}
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px', lineHeight: 1.6, padding: '8px 10px', background: 'color-mix(in srgb, var(--amber) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--amber) 20%, transparent)', borderRadius: 'var(--radius-sm)' }}>
                    ℹ️ Report Analyzer is built for <strong>Ad Server</strong> reports — only your saved Ad Server configs appear in the dropdown below. Demand and supply configs are hidden because they don't produce the right shape for this analysis.
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px', lineHeight: 1.6, padding: '8px 10px', background: 'color-mix(in srgb, var(--cyan) 4%, transparent)', border: '1px solid color-mix(in srgb, var(--cyan) 15%, transparent)', borderRadius: 'var(--radius-sm)' }}>
                    💡 API fetch returns data <strong>already aggregated</strong> by your selected dimensions — typically 500–5,000 rows instead of 100k+ raw events. Faster, cheaper, and more accurate analysis.
                  </div>
                  <FetchFromAPIPanel
                    onDataReady={handleApiData}
                    onCancel={() => setUploadMode('file')} />
                </div>
              )}
              {uploadMode === 'file' && (
                <>
                  {/* Ad-Server-only disclaimer for File mode */}
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px', lineHeight: 1.6, padding: '8px 10px', background: 'color-mix(in srgb, var(--amber) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--amber) 20%, transparent)', borderRadius: 'var(--radius-sm)' }}>
                    ℹ️ Report Analyzer is built for <strong>Ad Server</strong> reports. Upload reports from your ad server (e.g. exported CSV/XLSX) for best results. Reports from demand or supply partners may not yield meaningful insights.
                  </div>
                  <UploadZone onFile={handleFile} />
                </>
              )}
            </div>
          ) : (
            <div>
              {/* Preview — skip for XLSX before analysis */}
              {parsedData && !parsedData.isXlsx ? (
                <div style={{ marginBottom: '16px' }}>
                  <ReportPreview parsedData={parsedData} fileName={fileName} onReset={reset} />
                </div>
              ) : (
                <div className="card" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FileText size={18} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text-primary)' }}>{fileName}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>XLSX file ready — columns and rows will be parsed on analysis</div>
                  </div>
                  <button onClick={reset} className="btn btn-secondary btn-sm"><X size={11}/> Remove</button>
                </div>
              )}

              {error && (
                <div className="alert alert-error" style={{ marginBottom: '14px' }}>
                  <XCircle size={13} /> {error}
                </div>
              )}

              <button
                className="btn btn-primary"
                onClick={runAnalysis}
                disabled={!canAnalyze}
                style={{ width: '100%', padding: '12px', fontSize: '14px', fontWeight: 700, justifyContent: 'center', gap: '8px' }}
              >
                <Brain size={16} /> Analyze Report with AI
              </button>

              <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
                Powered by Claude — your data is sent securely and not stored
              </div>
            </div>
          )}
        </>
      )}

      {/* Analyzing state */}
      {analyzing && <AnalyzingState />}

      {/* Results */}
      {!analyzing && analysis && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px', gap: '8px' }}>
            <button className="btn btn-secondary btn-sm" onClick={reset}>
              <Upload size={12} /> Upload New Report
            </button>
            <button className="btn btn-secondary btn-sm" onClick={runAnalysis} disabled={analyzing}>
              <RefreshCw size={12} /> Re-analyze
            </button>
          </div>
          <AnalysisResults
            analysis={analysis}
            reportSnippet={reportSnippet}
            analysisSummary={analysisSummary}
            onReanalyze={runAnalysis}
          />
        </div>
      )}
    </div>
  );
}
